import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { me } from "../authService";
import {
  getProfile,
  updateProfile,
  createPhone,
  updatePhone,
  deletePhone,
  createMessenger,
  deleteMessenger,
  uploadAvatar,
  deleteAvatar,
} from "../profileService";
import { getInitials } from "../../../utils/initials";
import FormField from "../../../Components/Admin/FormField";
import ThemeSelector from "../../../Components/ThemeSelector";
import Badge from "../../../Components/Admin/Badge";
import MessengerIcon from "../../../Components/Admin/MessengerIcon";
import Modal from "../../../Components/Admin/Modal";
import IconButton from "../../../Components/Admin/IconButton";
import {
  FormActions,
  PrimaryButton,
  SecondaryButton,
} from "../../../Components/Admin/FormActions";
import styled from "styled-components";
import { media } from "../../../styles/breakpoints";
import { font } from "../../../styles/typography";
import pkg from "../../../../package.json";
import { browserNotifyPermission, requestBrowserNotifyPermission } from "../../../utils/browserNotify";
import { pushSupport, isPushActive, syncPushState, enablePush, disablePush } from "../../../utils/pushNotify";

// ──────────────── Styled ────────────────

const Wrapper = styled.div`
  box-sizing: border-box;
  max-width: 800px;
  margin: 0 auto;
  padding: 0 32px 40px;

  @media (max-width: 640px) {
    padding: 0 16px 40px;
  }
`;

const Loading = styled.div`
  padding: 48px;
  text-align: center;
  color: var(--spark-color-control-disabled-text);
`;

const ErrorBlock = styled.div`
  padding: 48px;
  text-align: center;
  color: var(--spark-color-error-text);
`;

const Card = styled.div`
  background: var(--spark-color-surface);
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 20px;
  box-shadow: 0px 0px 6px 4px var(--spark-color-shadow);
`;

const CardTitle = styled.h2`
  margin: 0 0 16px;
  ${font('xl')}
  font-weight: 600;
  color: var(--spark-color-text);
`;

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 16px;

  // На телефоне поля форм схлопываем в одну колонку.
  ${media.mobile`
    grid-template-columns: 1fr;
  `}
`;

const SuccessMsg = styled.div`
  padding: 10px 16px;
  background: var(--spark-color-success-surface);
  color: var(--spark-color-success-text);
  border-radius: 6px;
  ${font('md')}
  margin-bottom: 16px;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid var(--spark-color-border-subtle);
  ${font('base')}

  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.span`
  color: var(--spark-color-text-muted);
  font-weight: 500;
`;

const InfoValue = styled.span`
  color: var(--spark-color-text-control);
`;

const SwitchBlock = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--spark-color-border-subtle);
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SwitchButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 20px;
  background: var(--spark-color-info-surface);
  color: var(--spark-color-info-text);
  border: 1px solid var(--spark-color-info-border);
  border-radius: 6px;
  ${font('base')}
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: var(--spark-color-info-hover-surface);
  }
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  ${font('md')}
  color: var(--spark-color-text-secondary);
  cursor: pointer;

  input {
    margin: 0;
  }
`;

const AvatarSection = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 16px;
`;

const AvatarCircle = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  ${font('kpi')}
  font-weight: 600;
  color: var(--spark-color-on-brand);
  background: var(--spark-color-brand);
  user-select: none;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

// ──────────────── Constants ────────────────

const LABELS = {
  PERSONAL: "Особистий",
  WORK: "Робочий",
  INTERNAL: "Внутрішній",
  TELEGRAM: "Telegram",
  WHATSAPP: "WhatsApp",
  VIBER: "Viber",
};

const PHONE_TYPE_OPTIONS = [
  { value: "PERSONAL", label: "Особистий" },
  { value: "WORK", label: "Робочий" },
  { value: "INTERNAL", label: "Внутрішній" },
];

const MESSENGER_TYPE_OPTIONS = [
  { value: "TELEGRAM", label: "Telegram" },
  { value: "WHATSAPP", label: "WhatsApp" },
  { value: "VIBER", label: "Viber" },
];

const NOTIFICATION_EVENTS = [
  { key: 'ORDER_ASSIGNED_EXECUTOR', label: 'Призначено виконавця заявки (комплектація)', group: 'Заявки на комплектацію' },
  { key: 'ORDER_READY_FOR_PICKING', label: 'Заявка готова до комплектації (в т.ч. нові без виконавця)', group: 'Заявки на комплектацію' },
  { key: 'ORDER_PICKING_STARTED', label: 'Заявку взято в комплектацію', group: 'Заявки на комплектацію' },
  { key: 'ORDER_READY_FOR_SHIPMENT', label: 'Заявка готова до відвантаження', group: 'Заявки на комплектацію' },
  { key: 'ORDER_SHIPPED', label: 'Заявку відвантажено', group: 'Заявки на комплектацію' },
  { key: 'ORDER_DELIVERED', label: 'Заявку доставлено', group: 'Заявки на комплектацію' },
  { key: 'ORDER_ISSUE', label: 'Проблема із заявкою', group: 'Заявки на комплектацію' },
  { key: 'ORDER_CANCELED', label: 'Заявку скасовано', group: 'Заявки на комплектацію' },
  { key: 'ORDER_ASSIGNED_PRESALE', label: 'Призначено виконавця передпродажної підготовки', group: 'Передпродажна підготовка' },
  { key: 'ORDER_PRE_SALE_STARTED', label: 'Заявку переведено в передпродажну підготовку', group: 'Передпродажна підготовка' },
  { key: 'INQUIRY_ESCALATED', label: 'Питання куратору за зверненням (я — куратор напрямку)', group: 'Звернення' },
  { key: 'INQUIRY_DECIDED', label: 'Рішення куратора за моїм питанням', group: 'Звернення' },
  { key: 'INQUIRY_CURATOR_NOTIFIED', label: 'Менеджер повідомив мене як куратора за зверненням', group: 'Звернення' },
  { key: 'INQUIRY_ATTENTION_RAISED', label: 'Потрібна реакція менеджера за зверненням (пінок: мені або черзі мого підрозділу)', group: 'Звернення' },
  { key: 'INQUIRY_ASSIGNED', label: 'Мене призначено відповідальним за звернення', group: 'Звернення' },
  { key: 'INQUIRY_QUEUED', label: 'Нове звернення в черзі мого підрозділу (без відповідального — можна взяти в роботу)', group: 'Звернення' },
  { key: 'INQUIRY_COMMENTED', label: 'Новий коментар від іншого користувача у моєму зверненні', group: 'Звернення' },
  { key: 'INQUIRY_QUEUED', label: 'Нове звернення в черзі мого підрозділу (без відповідального — можна взяти в роботу)', group: 'Звернення' },
  { key: 'INQUIRY_STAGE_CHANGED', label: 'Змінено стадію мого звернення (в т.ч. автоматично з 1С)', group: 'Звернення' },
  { key: 'PROM_ORDER_NEW', label: 'Нове або оплачене замовлення prom.ua без звернення (усім, хто бачить журнал; за замовчуванням вимкнено)', group: 'Замовлення prom.ua' },
  { key: 'PROM_ORDER_PAID', label: 'Оплачено замовлення prom.ua за моїм зверненням', group: 'Замовлення prom.ua' },
  { key: 'PROM_ORDER_CANCELED', label: 'Скасовано замовлення prom.ua за моїм зверненням', group: 'Замовлення prom.ua' },
];

const NOTIFICATION_GROUPS = ['Заявки на комплектацію', 'Передпродажна підготовка', 'Звернення', 'Замовлення prom.ua'];

// Події, увімкнені за замовчуванням, якщо користувач ще не зберігав явний вибір
// (дзеркало back/src/constants/notification-events.js → DEFAULT_ENABLED_EVENTS)
const DEFAULT_ENABLED_EVENTS = [
  'ORDER_ASSIGNED_EXECUTOR',
  'ORDER_ASSIGNED_PRESALE',
  'ORDER_PRE_SALE_STARTED',
  'ORDER_READY_FOR_PICKING',
  'ORDER_PICKING_STARTED',
  'ORDER_READY_FOR_SHIPMENT',
  'ORDER_SHIPPED',
  'ORDER_DELIVERED',
  'ORDER_ISSUE',
  'ORDER_CANCELED',
  'INQUIRY_ESCALATED',
  'INQUIRY_DECIDED',
  'INQUIRY_ASSIGNED',
  'INQUIRY_QUEUED',
  'INQUIRY_COMMENTED',
  'INQUIRY_CURATOR_NOTIFIED',
  'INQUIRY_ATTENTION_RAISED',
  'INQUIRY_QUEUED',
  'INQUIRY_STAGE_CHANGED',
  'PROM_ORDER_PAID',
  'PROM_ORDER_CANCELED',
];

// ──────────────── Component ────────────────

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3090';

const Profile = () => {
  const { setUser, user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarVersion, setAvatarVersion] = useState(0);
  const [showInHeader, setShowInHeader] = useState(false);
  const [success, setSuccess] = useState(null);

  // Дані з /auth/me
  const [meData, setMeData] = useState(null);
  // Повні дані з /users/:id (з посадою)
  const [fullProfile, setFullProfile] = useState(null);

  // Форма
  const [form, setForm] = useState({});
  const [phones, setPhones] = useState([]);
  const [notificationSettings, setNotificationSettings] = useState({});
  const [browserPermission, setBrowserPermission] = useState(browserNotifyPermission);
  const pushState = pushSupport();
  const [pushActive, setPushActive] = useState(isPushActive);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushError, setPushError] = useState("");
  useEffect(() => {
    if (pushState === "ready") syncPushState().then(setPushActive);
  }, [pushState]);
  const togglePush = async () => {
    setPushBusy(true);
    setPushError("");
    try {
      if (pushActive) await disablePush();
      else await enablePush();
      setPushActive(isPushActive());
      setBrowserPermission(browserNotifyPermission());
    } catch (e) {
      setPushError(e.message || "Не вдалося змінити налаштування push");
    } finally {
      setPushBusy(false);
    }
  };

  // Модалки
  const [phoneModalOpen, setPhoneModalOpen] = useState(false);
  const [editingPhone, setEditingPhone] = useState(null);
  const [phoneForm, setPhoneForm] = useState({
    phone: "",
    type: "PERSONAL",
    isPrimary: false,
  });
  const [messengerModal, setMessengerModal] = useState(null);
  const [messengerForm, setMessengerForm] = useState({
    type: "TELEGRAM",
    username: "",
  });

  const loadProfile = async () => {
    setLoading(true);
    setError(null);
    try {
      const authData = await me();
      if (!authData?.authenticated || !authData?.profile) {
        setError("Не авторизовано");
        return;
      }
      setMeData(authData);
      const userId = authData.profile.id;

      // Повні дані (з посадою, табельним номером тощо)
      const fullProfileData = userId ? await getProfile(userId) : null;
      setFullProfile(fullProfileData);

      const profile = authData.profile;
      // Нормализуем относительный путь аватара
      const avatar =
        profile.avatar && profile.avatar.startsWith('/users/')
          ? `${API_URL}${profile.avatar}`
          : (profile.avatar || '');

      setForm({
        firstName: profile.firstName || "",
        lastName: profile.lastName || "",
        middleName: profile.middleName || "",
        emailPersonal: profile.emailPersonal || "",
        birthDate: profile.birthDate ? profile.birthDate.slice(0, 10) : "",
        description: profile.description || "",
        matrixUser: profile.matrixUser || "",
        avatar,
      });
      setPhones(authData.phoneNumbers || []);
      setNotificationSettings(fullProfileData?.notificationSettings || {});
    } catch (err) {
      setError(err.message || "Помилка завантаження профілю");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProfile();
  }, []);

  // Синхронізація чекбокса з даними користувача з БД
  useEffect(() => {
    if (user?.id) {
      setShowInHeader(user.showSwitchUserInHeader === true);
    }
  }, [user?.id, user?.showSwitchUserInHeader]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !meData?.profile?.id) return;
    setAvatarUploading(true);
    try {
      const result = await uploadAvatar(meData.profile.id, file);
      setForm((prev) => ({ ...prev, avatar: `${API_URL}${result.avatar}` }));
      setAvatarVersion((prev) => prev + 1);
      setSuccess("Аватар оновлено");
      // Оновлюємо AuthContext (щоб хедер побачив новий аватар)
      const fresh = await me();
      if (fresh?.profile) setUser(fresh.profile);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      alert(err.message || "Помилка завантаження аватара");
    } finally {
      setAvatarUploading(false);
    }
  };

  const handleDeleteAvatar = async () => {
    if (!meData?.profile?.id) return;
    try {
      await deleteAvatar(meData.profile.id);
      setForm((prev) => ({ ...prev, avatar: "" }));
      setAvatarVersion((prev) => prev + 1);
      setSuccess("Аватар видалено");
      // Оновлюємо AuthContext
      const fresh = await me();
      if (fresh?.profile) setUser(fresh.profile);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      alert(err.message || "Помилка видалення аватара");
    }
  };

  const handleSave = async () => {
    if (!meData?.profile?.id) return;
    setSaving(true);
    setSuccess(null);
    try {
      const saveData = {
        firstName: form.firstName,
        lastName: form.lastName,
        middleName: form.middleName || null,
        emailPersonal: form.emailPersonal || null,
        birthDate: form.birthDate || null,
        description: form.description || null,
        matrixUser: form.matrixUser || null,
      };
      await updateProfile(meData.profile.id, saveData);
      setSuccess("Зміни збережено");
      // Оновлюємо AuthContext
      const fresh = await me();
      if (fresh?.profile) setUser(fresh.profile);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message || "Помилка збереження");
    } finally {
      setSaving(false);
    }
  };

  // ===================== Phones =====================

  const handleOpenAddPhone = () => {
    setEditingPhone(null);
    setPhoneForm({ phone: "", type: "PERSONAL", isPrimary: false });
    setPhoneModalOpen(true);
  };

  const handleOpenEditPhone = (phone) => {
    setEditingPhone(phone);
    setPhoneForm({
      phone: phone.phone,
      type: phone.type,
      isPrimary: phone.isPrimary,
    });
    setPhoneModalOpen(true);
  };

  const handleSavePhone = async () => {
    if (!phoneForm.phone || !meData?.profile?.id) return;
    try {
      if (editingPhone) {
        const updated = await updatePhone(
          meData.profile.id,
          editingPhone.id,
          phoneForm
        );
        setPhones((prev) =>
          prev.map((p) => (p.id === editingPhone.id ? updated : p))
        );
      } else {
        const created = await createPhone(meData.profile.id, phoneForm);
        setPhones((prev) => [...prev, created]);
      }
      setPhoneModalOpen(false);
    } catch (err) {
      alert(err.message || "Помилка збереження телефону");
    }
  };

  const handleDeletePhone = async (phoneId) => {
    if (!meData?.profile?.id) return;
    try {
      await deletePhone(meData.profile.id, phoneId);
      setPhones((prev) => prev.filter((p) => p.id !== phoneId));
    } catch (err) {
      alert(err.message || "Помилка видалення телефону");
    }
  };

  // ===================== Messengers =====================

  const handleOpenAddMessenger = (phoneId) => {
    setMessengerModal(phoneId);
    setMessengerForm({ type: "TELEGRAM", username: "" });
  };

  const handleSaveMessenger = async () => {
    if (!messengerModal || !meData?.profile?.id) return;
    try {
      await createMessenger(meData.profile.id, messengerModal, messengerForm);
      // Оновлюємо телефони (з месенджерами)
      const fresh = await getProfile(meData.profile.id);
      setPhones(fresh.phoneNumbers || []);
      setMessengerModal(null);
    } catch (err) {
      alert(err.message || "Помилка додавання месенджера");
    }
  };

  const handleDeleteMessenger = async (phoneId, messengerId) => {
    if (!meData?.profile?.id) return;
    try {
      await deleteMessenger(meData.profile.id, phoneId, messengerId);
      const fresh = await getProfile(meData.profile.id);
      setPhones(fresh.phoneNumbers || []);
    } catch (err) {
      alert(err.message || "Помилка видалення месенджера");
    }
  };


  const handleSwitchUser = () => {
    window.location.href = `${API_URL}/auth/login`;
  };

  const handleToggleHeader = async (e) => {
    const checked = e.target.checked;
    setShowInHeader(checked);
    if (user?.id) {
      try {
        await updateProfile(user.id, { showSwitchUserInHeader: checked });
        setUser({ ...user, showSwitchUserInHeader: checked });
      } catch (err) {
        setShowInHeader(!checked);
        alert(err.message || "Помилка збереження налаштування");
      }
    }
  };

  const handleToggleNotification = async (eventKey, checked) => {
    const previous = notificationSettings;
    const updated = { ...notificationSettings, [eventKey]: checked };
    setNotificationSettings(updated);
    try {
      await updateProfile(meData.profile.id, { notificationSettings: updated });
    } catch (err) {
      setNotificationSettings(previous);
      alert(err.message || 'Помилка збереження налаштування');
    }
  };

  // ===================== Render =====================

  if (loading) return <Loading>Завантаження...</Loading>;
  if (error && !meData) {
    return <ErrorBlock>⚠️ {error}</ErrorBlock>;
  }

  const profile = meData?.profile;
  const full = fullProfile || profile;
  if (!profile) return <ErrorBlock>⚠️ Користувача не знайдено</ErrorBlock>;

  return (
    <Wrapper>
      <h1 style={{ fontSize: 20, fontWeight: 600, marginBottom: 20, color: "var(--spark-color-text)" }}>
        Особистий кабінет
      </h1>

      <Card>
        <CardTitle>Тема</CardTitle>
        <ThemeSelector showLabel={false} />
      </Card>

      {success && <SuccessMsg>{success}</SuccessMsg>}
      {error && (
        <div style={{ color: "var(--spark-color-error-text)", marginBottom: 16, fontSize: 13 }}>
          ⚠️ {error}
        </div>
      )}

      {/* ============ Аватар ============ */}
      <Card>
        <CardTitle>Фото профілю</CardTitle>
        <AvatarSection>
          <AvatarCircle>
            {form.avatar ? (
              <img src={`${form.avatar}?v=${avatarVersion}`} alt="avatar" />
            ) : (
              getInitials(form.firstName, form.lastName)
            )}
          </AvatarCircle>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <label
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "8px 16px",
                  borderRadius: 6,
                  background: "var(--spark-color-control-hover-surface)",
                  color: "var(--spark-color-text-control)",
                  fontSize: 14,
                  cursor: avatarUploading ? "not-allowed" : "pointer",
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                }}
              >
                {avatarUploading ? "Завантаження..." : "Завантажити фото"}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarChange}
                  disabled={avatarUploading}
                  style={{ display: "none" }}
                />
              </label>
              {form.avatar && (
                <button
                  type="button"
                  onClick={handleDeleteAvatar}
                  title="Видалити фото"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 32,
                    height: 32,
                    border: "1px solid var(--spark-color-control-border)",
                    borderRadius: 6,
                    background: "var(--spark-color-surface)",
                    cursor: "pointer",
                    color: "var(--spark-color-error-text)",
                    fontSize: 16,
                    lineHeight: 1,
                    padding: 0,
                  }}
                >
                  ✕
                </button>
              )}
            </div>
            <div style={{ fontSize: 12, color: "var(--spark-color-control-disabled-text)" }}>
              JPG, PNG, GIF, WebP до 5 MB
            </div>
          </div>
        </AvatarSection>
      </Card>

      {/* ============ Основная информация ============ */}
      <Card>
        <CardTitle>Основна інформація</CardTitle>
        <TwoCol>
          <FormField
            label="Ім'я"
            name="firstName"
            value={form.firstName}
            onChange={handleChange}
            required
          />
          <FormField
            label="Прізвище"
            name="lastName"
            value={form.lastName}
            onChange={handleChange}
            required
          />
          <FormField
            label="По батькові"
            name="middleName"
            value={form.middleName}
            onChange={handleChange}
          />
          <FormField
            label="Особистий email"
            name="emailPersonal"
            value={form.emailPersonal}
            onChange={handleChange}
            type="email"
          />
          <FormField
            label="Дата народження"
            name="birthDate"
            value={form.birthDate}
            onChange={handleChange}
            type="date"
          />
          <FormField
            label="Matrix користувач"
            name="matrixUser"
            value={form.matrixUser}
            onChange={handleChange}
          />
        </TwoCol>
        <FormField
          label="Про себе"
          name="description"
          value={form.description}
          onChange={handleChange}
          type="textarea"
        />
      </Card>

      {/* ============ Телефоны и мессенджеры ============ */}
      <Card>
        <CardTitle>Телефони та месенджери</CardTitle>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginBottom: 12,
          }}
        >
          <span style={{ fontWeight: 600, fontSize: 14, color: "var(--spark-color-text-secondary)" }}>
            Телефони
          </span>
          <SecondaryButton
            type="button"
            onClick={handleOpenAddPhone}
            style={{ padding: "4px 12px", fontSize: 12 }}
          >
            + Додати телефон
          </SecondaryButton>
        </div>

        {phones.length === 0 && (
          <div style={{ color: "var(--spark-color-control-disabled-text)", fontSize: 13, padding: "8px 0" }}>
            Немає телефонів
          </div>
        )}

        {phones.map((phone) => (
          <div
            key={phone.id}
            style={{
              border: "1px solid var(--spark-color-border)",
              borderRadius: 6,
              padding: 12,
              marginBottom: 8,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              <strong>{phone.phone}</strong>
              <Badge role={phone.type} />
              {phone.isPrimary && (
                <span
                  style={{ fontSize: 11, color: "var(--spark-color-success-text)", fontWeight: 600 }}
                >
                  Основний
                </span>
              )}
              <span style={{ marginLeft: "auto", display: "flex", gap: 4 }}>
                <IconButton
                  icon="pen"
                  title="Редагувати"
                  onClick={() => handleOpenEditPhone(phone)}
                />
                <IconButton
                  icon="trash"
                  $danger
                  title="Видалити"
                  onClick={() => handleDeletePhone(phone.id)}
                />
                <SecondaryButton
                  type="button"
                  onClick={() => handleOpenAddMessenger(phone.id)}
                  style={{ padding: "2px 8px", fontSize: 11 }}
                >
                  + Месенджер
                </SecondaryButton>
              </span>
            </div>
            {phone.messengers?.length > 0 && (
              <div
                style={{
                  marginTop: 6,
                  paddingLeft: 8,
                  borderLeft: "2px solid var(--spark-color-border)",
                }}
              >
                {phone.messengers.map((m) => (
                  <div
                    key={m.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      fontSize: 12,
                      marginBottom: 4,
                    }}
                  >
                    <MessengerIcon type={m.type} />
                    <span>{m.username}</span>
                    <IconButton
                      icon="trash"
                      $danger
                      title="Видалити"
                      onClick={() =>
                        handleDeleteMessenger(phone.id, m.id)
                      }
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </Card>

      {/* ============ Учетные данные (read-only) ============ */}
      <Card>
        <CardTitle>Облікові дані</CardTitle>
        <InfoRow>
          <InfoLabel>Email для входу</InfoLabel>
          <InfoValue>{full.authEmail || "—"}</InfoValue>
        </InfoRow>
        <InfoRow>
          <InfoLabel>Роль</InfoLabel>
          <InfoValue>
            <Badge role={full.globalRole || "USER"} />
          </InfoValue>
        </InfoRow>
        <InfoRow>
          <InfoLabel>Відділ</InfoLabel>
          <InfoValue>{full.departmentName || "—"}</InfoValue>
        </InfoRow>
        <InfoRow>
          <InfoLabel>Посада</InfoLabel>
          <InfoValue>{full.jobTitleName || "—"}</InfoValue>
        </InfoRow>
        <InfoRow>
          <InfoLabel>Табельний номер</InfoLabel>
          <InfoValue>{full.employeeNumber || "—"}</InfoValue>
        </InfoRow>
        <InfoRow>
          <InfoLabel>Версія сайту</InfoLabel>
          <InfoValue>v{pkg.version}</InfoValue>
        </InfoRow>
        <SwitchBlock>
          <SwitchButton type="button" onClick={handleSwitchUser}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M16 3h5v5" />
              <path d="M8 3H3v5" />
              <path d="M3 16v5h5" />
              <path d="M21 16v5h-5" />
              <path d="M21 12h-4.05A3 3 0 0 0 14 14.95V21" />
              <path d="M3 12h4.05A3 3 0 0 1 10 9.05V3" />
            </svg>
            Змінити користувача
          </SwitchButton>
          <CheckboxRow>
            <input
              type="checkbox"
              checked={showInHeader}
              onChange={handleToggleHeader}
            />
            Показувати кнопку зміни користувача в хедері
          </CheckboxRow>
        </SwitchBlock>
      </Card>

      {/* ============ Push-сповіщення на телефон / закритий застосунок ============ */}
      {pushState !== "unsupported" && (
        <Card>
          <CardTitle>Push-сповіщення</CardTitle>
          <div style={{ fontSize: 14, color: 'var(--spark-color-text-secondary)', marginBottom: 8 }}>
            Ті самі сповіщення (звернення, дзвінки, замовлення prom.ua) приходять на цей пристрій, навіть коли застосунок закрито або телефон заблоковано.
          </div>
          {pushState === "ios-install" && (
            <div style={{ fontSize: 14, color: 'var(--spark-color-warning-text)' }}>
              На iPhone/iPad push працює лише в застосунку з екрана «Додому»: натисніть «Поділитися» → «На екран Додому», відкрийте SPARK з іконки і поверніться сюди (потрібна iOS 16.4+).
            </div>
          )}
          {pushState === "ready" && (
            <>
              {pushActive && <div style={{ fontSize: 14, color: 'var(--spark-color-success-text)', fontWeight: 500, marginBottom: 8 }}>✓ Увімкнено на цьому пристрої</div>}
              {browserPermission === 'denied' && !pushActive ? (
                <div style={{ fontSize: 14, color: 'var(--spark-color-error-text)' }}>
                  Заблоковано в браузері — дозвольте сповіщення для цього сайту в налаштуваннях пристрою або браузера.
                </div>
              ) : (
                <PrimaryButton type="button" disabled={pushBusy} onClick={togglePush}>
                  {pushActive ? 'Вимкнути push' : 'Увімкнути push на цьому пристрої'}
                </PrimaryButton>
              )}
              {pushError && <div style={{ fontSize: 14, color: 'var(--spark-color-error-text)', marginTop: 8 }}>{pushError}</div>}
            </>
          )}
        </Card>
      )}

      {/* ============ Сповіщення в браузері ============ */}
      {browserPermission !== 'unsupported' && (
        <Card>
          <CardTitle>Сповіщення в браузері</CardTitle>
          <div style={{ fontSize: 14, color: 'var(--spark-color-text-secondary)', marginBottom: 8 }}>
            Системні сповіщення про звернення, дзвінки та замовлення prom.ua, коли вкладка SPARK у фоні.
            Працюють, поки вкладка відкрита.
          </div>
          {browserPermission === 'granted' && <div style={{ fontSize: 14, color: 'var(--spark-color-success-text)', fontWeight: 500 }}>✓ Увімкнено</div>}
          {browserPermission === 'denied' && (
            <div style={{ fontSize: 14, color: 'var(--spark-color-error-text)' }}>
              Заблоковано в браузері — дозвольте сповіщення для цього сайту в налаштуваннях браузера (значок замка в адресному рядку).
            </div>
          )}
          {browserPermission === 'default' && (
            <PrimaryButton type="button" onClick={async () => setBrowserPermission(await requestBrowserNotifyPermission())}>
              Увімкнути сповіщення
            </PrimaryButton>
          )}
        </Card>
      )}

      {/* ============ Сповіщення ============ */}
      <Card>
        <CardTitle>Сповіщення на email</CardTitle>
        {NOTIFICATION_GROUPS.map((groupName) => (
          <div key={groupName} style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--spark-color-text-muted)', textTransform: 'uppercase', marginBottom: 8 }}>
              {groupName}
            </div>
            {NOTIFICATION_EVENTS.filter((e) => e.group === groupName).map((evt) => (
              <CheckboxRow key={evt.key}>
                <input
                  type="checkbox"
                  checked={notificationSettings[evt.key] === true || (notificationSettings[evt.key] === undefined && DEFAULT_ENABLED_EVENTS.includes(evt.key))}
                  onChange={(e) => handleToggleNotification(evt.key, e.target.checked)}
                />
                {evt.label}
              </CheckboxRow>
            ))}
          </div>
        ))}
      </Card>

      {/* ============ Кнопка сохранить ============ */}
      <FormActions>
        <PrimaryButton onClick={handleSave} disabled={saving}>
          {saving ? "Збереження..." : "Зберегти зміни"}
        </PrimaryButton>
      </FormActions>

      {/* ============ Модалки ============ */}

      <Modal
        isOpen={phoneModalOpen}
        onClose={() => setPhoneModalOpen(false)}
        title={editingPhone ? "Редагувати телефон" : "Додати телефон"}
        size="sm"
      >
        <FormField
          label="Номер телефону"
          name="phone"
          value={phoneForm.phone}
          onChange={(f, v) =>
            setPhoneForm((prev) => ({ ...prev, phone: v }))
          }
        />
        <FormField
          label="Тип"
          name="type"
          value={phoneForm.type}
          onChange={(f, v) =>
            setPhoneForm((prev) => ({ ...prev, type: v }))
          }
          as="select"
          options={PHONE_TYPE_OPTIONS}
        />
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginTop: 12,
          }}
        >
          <input
            type="checkbox"
            id="phoneIsPrimary"
            checked={phoneForm.isPrimary}
            onChange={(e) =>
              setPhoneForm((prev) => ({
                ...prev,
                isPrimary: e.target.checked,
              }))
            }
          />
          <label htmlFor="phoneIsPrimary" style={{ fontSize: 14, color: "var(--spark-color-text-secondary)" }}>
            Основний телефон
          </label>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
            marginTop: 16,
          }}
        >
          <SecondaryButton
            type="button"
            onClick={() => setPhoneModalOpen(false)}
          >
            Скасувати
          </SecondaryButton>
          <PrimaryButton
            onClick={handleSavePhone}
            disabled={!phoneForm.phone}
          >
            {editingPhone ? "Зберегти" : "Додати"}
          </PrimaryButton>
        </div>
      </Modal>

      <Modal
        isOpen={!!messengerModal}
        onClose={() => setMessengerModal(null)}
        title="Додати месенджер"
        size="sm"
      >
        <FormField
          label="Тип"
          name="type"
          value={messengerForm.type}
          onChange={(f, v) =>
            setMessengerForm((prev) => ({ ...prev, type: v }))
          }
          as="select"
          options={MESSENGER_TYPE_OPTIONS}
        />
        <FormField
          label="Профіль (необов'язково)"
          name="username"
          value={messengerForm.username}
          onChange={(f, v) =>
            setMessengerForm((prev) => ({ ...prev, username: v }))
          }
        />
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
            marginTop: 16,
          }}
        >
          <SecondaryButton
            type="button"
            onClick={() => setMessengerModal(null)}
          >
            Скасувати
          </SecondaryButton>
          <PrimaryButton onClick={handleSaveMessenger}>
            Додати
          </PrimaryButton>
        </div>
      </Modal>
    </Wrapper>
  );
};

export default Profile;
